import { useMutation, useQueryClient, QueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { Layer, LayerSharingMode } from 'types'
import produce from 'immer'
import { buildErrorMessage } from 'utils'
import { layersKeys } from './layersKeys'
import { useArticlePassword } from 'hooks/useArticlePassword'

interface Attributes {
  name?: string
  public?: boolean
  sharing_mode?: LayerSharingMode
}

type CacheData = { layers: Layer[] } | undefined

export const useUpdateLayer = (articleKey: string, id: string) => {
  const queryClient = useQueryClient()
  const password = useArticlePassword(articleKey)

  return useMutation<{ layer: Layer }, Error, Attributes>(
    layersKeys.update(id),
    async (attributes) => {
      const response = await sendToApi(`/articles/${articleKey}/layers/${id}`, {
        method: 'PATCH',
        body: { name: attributes.name, public: attributes.public },
      })

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error(
          buildErrorMessage(response.data.errors) ||
            'An error occurred! Our team has been notified and we are looking into it.',
        )
      }
      return response.data
    },
    {
      onMutate: (variables) => {
        const currentLayer = queryClient
          .getQueryData<CacheData>(layersKeys.list(articleKey, { password }))
          ?.layers?.find((layer) => layer.id === id)
        // Optimistic update
        updateCache(password, queryClient, articleKey, id, variables)
        return currentLayer
      },
      onError: (_error, _variables, oldAttributes) => {
        updateCache(password, queryClient, articleKey, id, oldAttributes as Layer)
      },
      onSuccess: async () => {
        queryClient.invalidateQueries(layersKeys.list(articleKey, { password }))
      },
    },
  )
}

const updateCache = (
  password: string | undefined,
  queryClient: QueryClient,
  articleKey: string,
  id: string,
  attributes: Attributes,
) => {
  queryClient.setQueryData<{ layer: Layer } | undefined>(layersKeys.detail(id, password), (old) => {
    if (!old) {
      return old
    }
    return { layer: { ...old.layer, ...(attributes as Attributes) } }
  })
  queryClient.setQueryData<CacheData>(layersKeys.list(articleKey, { password }), (old) => {
    return produce(old, (draft) => {
      if (!draft) {
        return
      }
      const index = draft.layers.findIndex((layer) => layer.id === id)
      if (index !== -1) {
        draft.layers[index] = { ...draft.layers[index], ...(attributes as Attributes) }
      }
    })
  })
}
